/**
 * The activity feed.
 *
 * ## Why it refetches instead of replaying socket frames
 *
 * The board's state updates from the socket; the feed does not. It is a paginated
 * read model over `board_events`, and rebuilding it from broadcast frames would
 * mean reimplementing the server's event projection on the client - two
 * projections that drift, and the drift shows up as a feed that disagrees with the
 * cards beside it.
 *
 * Instead, any live frame raises `activityStale`, and `BoardProvider` refetches
 * page one behind a 1.2-second trailing debounce. A burst of ten card moves costs
 * one request rather than ten, and the feed is always the server's own view.
 *
 * ## Why cursor pagination and not "load more by offset"
 *
 * The feed reads newest-first while new events arrive at the top. With an offset,
 * a page fetched after three new events arrive repeats three rows the client
 * already has - a visible duplicate. The opaque cursor encodes "everything
 * strictly before sequence 41", which is stable under insertion. See
 * `packages/shared/src/cursor.ts`.
 */

import { useMemo, type ReactNode } from 'react';
import type { BoardEvent, PublicUser } from '@slate/shared';
import { useBoard } from '../state/BoardProvider.js';
import { Avatar } from './Avatar.js';
import { Icon, ghostButtonClass, useTick } from './ui.js';
import { relativeTime } from '../lib/format.js';

export function ActivityFeed({ onClose }: { onClose(): void }): ReactNode {
  const { state, actions } = useBoard();

  // Relative timestamps only need to be fresh while someone is looking at them.
  const now = useTick(true, 30_000);

  const members = useMemo(() => {
    const map = new Map<string, PublicUser>();
    for (const member of state.members) map.set(member.user.id, member.user);
    return map;
  }, [state.members]);

  const describeContext = useMemo<DescribeContext>(
    () => ({
      members,
      columnNames: new Map(state.columns.map((column) => [column.id, column.name])),
    }),
    [members, state.columns],
  );

  const newest = state.activity[0];

  return (
    <aside
      aria-label="Activity"
      className="flex w-[22rem] shrink-0 flex-col border-l border-border bg-surface"
    >
      <header className="flex items-center gap-2 border-b border-border px-3 py-2.5">
        <h2 className="text-sm font-semibold text-ink">Activity</h2>
        {newest !== undefined && (
          <span className="text-[11px] text-ink-subtle">latest #{newest.sequence}</span>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close activity"
          className="ml-auto rounded p-1 text-ink-subtle hover:bg-sunken hover:text-ink"
        >
          <Icon name="close" size={14} />
        </button>
      </header>

      <ol className="scroll-slim min-h-0 flex-1 overflow-y-auto px-3 py-2">
        {state.activity.length === 0 && (
          <li className="px-1 py-6 text-center text-xs text-ink-subtle">Nothing has happened on this board yet.</li>
        )}

        {state.activity.map((event) => {
          const described = describeEvent(event, describeContext);
          return (
            <li key={event.id} className="flex gap-2.5 border-b border-border/60 py-2.5 last:border-b-0">
              <Avatar name={described.actorName} colour={described.actorColour} size={22} title={described.actorName} />
              <div className="min-w-0 flex-1">
                <p className="text-xs leading-snug text-ink-muted">
                  <span className="font-medium text-ink">{described.actorName}</span> {described.text}
                </p>
                <p className="mt-0.5 text-[11px] text-ink-subtle">
                  <time dateTime={event.createdAt}>{relativeTime(event.createdAt, now)}</time>
                  <span className="mx-1">·</span>
                  <span className="font-mono">#{event.sequence}</span>
                </p>
              </div>
            </li>
          );
        })}
      </ol>

      <footer className="border-t border-border p-2">
        {state.activityCursor === null ? (
          <p className="px-2 py-1 text-center text-[11px] text-ink-subtle">That is the whole history.</p>
        ) : (
          <button
            type="button"
            onClick={() => void actions.loadActivity()}
            className={`${ghostButtonClass} w-full`}
          >
            Load older activity
          </button>
        )}
      </footer>
    </aside>
  );
}

interface Described {
  actorName: string;
  actorColour: string;
  text: string;
}

/**
 * Everything `describe` needs to turn an event into a sentence.
 *
 * Passed as one object rather than as positional arguments because the three
 * lookups are all "resolve an id to something a human recognises", and a caller
 * that has one of them almost always has the others - the board's snapshot
 * carries members, columns and labels together.
 */
export interface DescribeContext {
  members: Map<string, PublicUser>;
  columnNames: Map<string, string>;
}

/** A field of the event's JSON payload, or `null`.
 *
 * The payload is an untyped blob written by the server, so every read goes through
 * here. A missing field produces a less specific sentence rather than an exception
 * inside a list render - which is the failure mode that takes out the whole feed
 * instead of one row. */
function payloadField(event: BoardEvent, key: string): unknown {
  return event.payload[key];
}

function payloadString(event: BoardEvent, key: string): string | null {
  const value = payloadField(event, key);
  return typeof value === 'string' ? value : null;
}

/** `payload.card.title`. */
function cardTitle(event: BoardEvent): string | null {
  const card = payloadField(event, 'card');
  if (typeof card === 'object' && card !== null && 'title' in card) {
    const title = (card as { title?: unknown }).title;
    if (typeof title === 'string') return title;
  }
  return payloadString(event, 'title');
}

/** The column a card event concerns, resolved to a name where possible.
 *
 * The event payload carries the card, not the column it landed in - the column is
 * identified by id, and an id means nothing in a sentence. Resolving it against
 * the board's current columns is correct for the overwhelmingly common case and
 * degrades to "another column" for an event whose column has since been deleted,
 * which is the honest answer. */
function columnName(event: BoardEvent, columns: Map<string, string>): string | null {
  const card = payloadField(event, 'card');
  if (typeof card === 'object' && card !== null && 'columnId' in card) {
    const columnId = (card as { columnId?: unknown }).columnId;
    if (typeof columnId === 'string') return columns.get(columnId) ?? null;
  }

  const column = payloadField(event, 'column');
  if (typeof column === 'object' && column !== null && 'name' in column) {
    const name = (column as { name?: unknown }).name;
    if (typeof name === 'string') return name;
  }
  return payloadString(event, 'name');
}

/**
 * Turn an event into a sentence.
 *
 * The switch is exhaustive over `BoardEventType` with no `default`, so adding an
 * event type on the server is a TypeScript error here rather than a feed entry
 * that renders as the raw type string. That is the same technique the Durable
 * Object uses for its mutation switch, and it is why a server-first deploy is
 * safe: the client will not compile until it has been taught the new event.
 */
export function describeEvent(event: BoardEvent, context: DescribeContext): Described {
  const actor = event.actorId === null ? null : (context.members.get(event.actorId) ?? null);
  const actorName = actor?.name ?? 'Someone';
  const actorColour = actor?.colour ?? '#94a3b8';

  const card = cardTitle(event);
  const column = columnName(event, context.columnNames);

  switch (event.type) {
    case 'board.created':
      return { actorName, actorColour, text: 'created this board.' };
    case 'board.updated':
      return { actorName, actorColour, text: 'updated the board details.' };
    case 'board.archived':
      return { actorName, actorColour, text: 'archived this board.' };
    case 'column.created':
      return { actorName, actorColour, text: `added the column ${column ?? 'a column'}.` };
    case 'column.updated':
      return { actorName, actorColour, text: `renamed a column to ${column ?? 'something else'}.` };
    case 'column.reordered':
      return { actorName, actorColour, text: 'reordered the columns.' };
    case 'column.deleted':
      return { actorName, actorColour, text: `deleted the column ${column ?? 'a column'}.` };
    case 'card.created':
      return { actorName, actorColour, text: `added ${card ?? 'a card'}.` };
    case 'card.moved':
      return {
        actorName,
        actorColour,
        text: `moved ${card ?? 'a card'}${column === null ? '' : ` to ${column}`}.`,
      };
    case 'card.updated':
      return { actorName, actorColour, text: `edited ${card ?? 'a card'}.` };
    case 'card.archived':
      return { actorName, actorColour, text: `archived ${card ?? 'a card'}.` };
    case 'card.deleted':
      return { actorName, actorColour, text: `deleted ${card ?? 'a card'}.` };
    case 'comment.created':
      return { actorName, actorColour, text: `commented on ${card ?? 'a card'}.` };
    case 'comment.updated':
      return { actorName, actorColour, text: 'edited a comment.' };
    case 'comment.deleted':
      return { actorName, actorColour, text: 'deleted a comment.' };
    case 'attachment.created':
      return { actorName, actorColour, text: `attached a file to ${card ?? 'a card'}.` };
    case 'member.joined':
      return { actorName, actorColour, text: 'joined the workspace.' };
  }
}
